import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Eye, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ObserverTenderView() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/observer/tenders/${tenderId}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Failed'))
      .finally(() => setLoading(false));
  }, [tenderId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin h-8 w-8 text-brandpurple" /></div>;
  const t = data?.tender || {};
  const bids = data?.bids || [];
  const cw = t.committee_workflow || {};
  const sub = (cw.submissions || []).find(s => s.id === cw.active_submission_id);
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="observer-tender-view">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate('/observer/dashboard')} className="text-sm text-brandpurple font-semibold mb-4 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-4">
          <div className="flex items-center gap-2 mb-2"><Eye className="w-4 h-4 text-brandpurple" /><span className="text-xs font-bold uppercase text-brandpurple">View Only</span></div>
          <h1 className="text-2xl font-black text-navy">{t.title}</h1>
          <div className="text-sm text-slate-600 mt-1">{t.work_category} · {t.location} · Status: <strong className="text-brandpurple">{t.status}</strong></div>
          <div className="text-xs text-slate-500 mt-1">Estimated Budget: {t.estimated_value ? `₹${Number(t.estimated_value).toLocaleString('en-IN')}` : 'Not Available'}</div>
        </div>
        {sub && (
          <Card title="Committee Status">
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div>Status: <strong className="text-brandpurple">{sub.status?.toUpperCase()}</strong></div>
              <div>Approvals / Rejections: <strong>{sub.approvals_count || 0} approved · {sub.rejections_count || 0} rejected</strong> of {sub.total_members}</div>
              <div>Minimum needed: <strong>{sub.min_approvals}</strong></div>
              <div>Recommended: <strong>{sub.recommended_supplier_name || '—'}</strong> {sub.recommended_amount ? `· ₹${Number(sub.recommended_amount).toLocaleString('en-IN')}` : ''}</div>
            </div>
          </Card>
        )}
        <Card title="Bids (Public Comparative)">
          {bids.length === 0 ? <div className="text-sm text-slate-500">No bids yet.</div> : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b"><tr><th className="text-left py-2">Supplier</th><th className="text-right">Quoted Amount</th><th className="text-center">Status</th></tr></thead>
              <tbody>{bids.map(b => (
                <tr key={b.id} className="border-b"><td className="py-2">{b.supplier?.company_name || b.supplier_id}</td><td className="text-right">₹{Number(b.quoted_amount || 0).toLocaleString('en-IN')}</td><td className="text-center text-xs">{b.status || '—'}</td></tr>
              ))}</tbody>
            </table>
          )}
        </Card>
        {t.status === 'awarded' && (
          <Card title="Award Status">
            <div className="text-sm">Awarded to <strong>{t.awarded_supplier_id}</strong> at <strong>₹{Number(t.awarded_amount || 0).toLocaleString('en-IN')}</strong></div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-4">
      <h2 className="text-lg font-bold text-navy mb-4">{title}</h2>
      {children}
    </div>
  );
}
